import { Star, MapPin, Clock } from "lucide-react";
import { SITE, waLink } from "../data/site";
import { useT } from "../i18n/LanguageContext";

export const Hero = () => {
  const { t } = useT();
  return (
    <section id="hero" data-testid="hero-section" className="relative pt-[78px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-12 lg:pt-20 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — copy */}
          <div className="lg:col-span-6 relative">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-12 bg-[var(--gold)]" />
              <span className="text-[11px] tracking-[0.32em] uppercase text-[var(--gold-deep)] font-medium">
                {t("hero.kicker")}
              </span>
            </div>

            <h1 className="font-display text-[52px] sm:text-[68px] lg:text-[84px] leading-[0.95] text-[var(--espresso)] tracking-tight-elegant">
              Pizzeria<br/>
              <span className="italic font-serif-elegant text-[var(--gold-deep)]">da&nbsp;Sofia</span>
            </h1>

            <p className="mt-7 text-lg lg:text-xl text-[var(--espresso-soft)] max-w-xl leading-relaxed font-normal">
              {t("hero.sub1")} <span className="italic font-medium">{t("hero.sub2")}</span>{t("hero.sub3")}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={waLink(t("waMessages.book"))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="btn-primary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                {t("hero.ctaWa")}
              </a>
              <a href="#menu" className="btn-ghost" data-testid="hero-cta-menu">
                {t("hero.ctaMenu")}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-5 max-w-xl">
              <div className="border-l border-[var(--gold)]/40 pl-4">
                <div className="flex items-center gap-1 text-[var(--gold-deep)]">
                  {Array.from({length:5}).map((_,i)=>(<Star key={i} className="w-3.5 h-3.5" fill="currentColor" />))}
                </div>
                <div className="mt-1 font-display text-2xl text-[var(--espresso)]">{SITE.rating.stars}</div>
                <div className="text-[11px] uppercase tracking-widest text-[var(--warm-gray)]">{t("hero.stats.reviews")}</div>
              </div>
              <div className="border-l border-[var(--gold)]/40 pl-4">
                <Clock className="w-4 h-4 text-[var(--gold-deep)]" />
                <div className="mt-1 font-display text-2xl text-[var(--espresso)]">36h</div>
                <div className="text-[11px] uppercase tracking-widest text-[var(--warm-gray)]">{t("hero.stats.leavening")}</div>
              </div>
              <div className="border-l border-[var(--gold)]/40 pl-4">
                <MapPin className="w-4 h-4 text-[var(--gold-deep)]" />
                <div className="mt-1 font-display text-2xl text-[var(--espresso)]">Novara</div>
                <div className="text-[11px] uppercase tracking-widest text-[var(--warm-gray)]">{t("hero.stats.addressLine")}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] max-w-[560px] mx-auto">
              <div className="absolute -inset-3 border border-[var(--gold)]/40 rounded-[2px] translate-x-4 translate-y-4" />
              <div className="relative w-full h-full overflow-hidden rounded-[2px] shadow-[0_30px_80px_-20px_rgba(42,31,24,0.35)]">
                <img
                  src={SITE.images.pizzaDiavola}
                  alt="Pizzeria da Sofia"
                  className="w-full h-full object-cover"
                  data-testid="hero-main-image"
                />
                <div className="absolute bottom-4 left-4 bg-[var(--ivory)]/95 backdrop-blur-sm px-5 py-3 rounded-full flex items-center gap-2 shadow">
                  <Star className="w-4 h-4 text-[var(--gold-deep)]" fill="currentColor" />
                  <span className="font-display text-sm text-[var(--espresso)]">4,6 / 5</span>
                  <span className="text-[10px] uppercase tracking-wider text-[var(--warm-gray)]">Google</span>
                </div>
              </div>

              <div className="hidden md:block absolute -left-8 -bottom-8 bg-[var(--ivory)] p-4 w-44 rounded-[2px] shadow-[0_18px_40px_-12px_rgba(42,31,24,0.25)] rotate-[-3deg]">
                <img src={SITE.images.pizzaPesto} alt="" className="w-full h-28 object-cover" />
                <div className="mt-2 font-serif-elegant italic text-sm text-[var(--espresso)]">La Genovese</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--sand)]/70" />
    </section>
  );
};
